import { useEffect, useState } from 'react';
import type { AnnouncementDisplay } from '@quality-vet/contracts';

import { Modal } from './modal';
import '../announcements.css';

function dismissalKey(userId: string, announcement: AnnouncementDisplay): string {
  return `quality-vet:announcement-dismissed:${userId}:${announcement.id}:${announcement.revision}`;
}

function wasDismissed(userId: string, announcement: AnnouncementDisplay): boolean {
  try {
    return window.localStorage.getItem(dismissalKey(userId, announcement)) === '1';
  } catch {
    return false;
  }
}

export function AnnouncementModalLayer({
  apiUrl,
  accessToken,
  userId,
}: {
  apiUrl: string;
  accessToken: string;
  userId: string;
}) {
  const [announcementQueue, setAnnouncementQueue] = useState<AnnouncementDisplay[]>([]);
  const [acknowledgementBusy, setAcknowledgementBusy] = useState(false);
  const [acknowledgementError, setAcknowledgementError] = useState('');

  useEffect(() => {
    const abortController = new AbortController();
    setAnnouncementQueue([]);
    void (async () => {
      try {
        const response = await fetch(`${apiUrl}/v1/announcements`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: abortController.signal,
        });
        if (!response.ok) return;
        const payload = (await response.json()) as { items?: AnnouncementDisplay[] };
        const eligible = (payload.items ?? []).filter((item) =>
          item.acknowledgementRequired ? !item.acknowledgedAt : !wasDismissed(userId, item),
        );
        if (!abortController.signal.aborted) setAnnouncementQueue(eligible);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
      }
    })();
    return () => abortController.abort();
  }, [accessToken, apiUrl, userId]);

  const announcement = announcementQueue[0];
  if (!announcement) return null;

  const dismiss = () => {
    if (announcement.acknowledgementRequired) return;
    try {
      window.localStorage.setItem(dismissalKey(userId, announcement), '1');
    } catch {
      // A private browsing policy may block storage; the modal still closes for this page load.
    }
    setAnnouncementQueue((current) =>
      current.filter((queuedAnnouncement) => queuedAnnouncement.id !== announcement.id),
    );
  };

  const acknowledge = async () => {
    setAcknowledgementBusy(true);
    setAcknowledgementError('');
    try {
      const response = await fetch(
        `${apiUrl}/v1/announcements/${encodeURIComponent(announcement.id)}/acknowledge`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) throw new Error(`Acknowledgement failed (${response.status})`);
      setAnnouncementQueue((current) =>
        current.filter((queuedAnnouncement) => queuedAnnouncement.id !== announcement.id),
      );
    } catch (error) {
      setAcknowledgementError(
        error instanceof Error ? error.message : 'Acknowledgement could not be recorded.',
      );
    } finally {
      setAcknowledgementBusy(false);
    }
  };

  return (
    <Modal
      closeDisabled={announcement.acknowledgementRequired}
      label={announcement.title}
      onClose={dismiss}
    >
      <div className="announcement-modal-content">
        <p className="eyebrow">Announcement</p>
        <h2>{announcement.title}</h2>
        <p className="announcement-message">{announcement.message}</p>
        {acknowledgementError ? <p role="alert">{acknowledgementError}</p> : null}
        <button
          type="button"
          onClick={() => (announcement.acknowledgementRequired ? void acknowledge() : dismiss())}
          disabled={acknowledgementBusy}
        >
          {announcement.acknowledgementRequired
            ? acknowledgementBusy
              ? 'Recording…'
              : 'Acknowledge'
            : 'Got it'}
        </button>
      </div>
    </Modal>
  );
}
